import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core";
import { Button } from "../Button";

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  value?: string;
  error?: string | null;
  placeholder?: string;
  className?: string;
  faIcon?: IconProp;
  iconSrc?: string;
  iconText?: string;
  iconClassName?: string;
  isRequired?: boolean;
  variant?: "default" | "auth";
  onButtonClick?: (value?: string) => void;
  disabled?: boolean;
  inputClassName?: string;
  faIconClassName?: string;
  faIconSize?: SizeProp;
  helperText?: string | null;
  helperTextClassName?: string | null;
  helperCallback?: () => void;
  pattern?: string;
  showBorder?: boolean;
  isFocusVisible?: boolean;
  iconPosition?: "right" | "left";
  autoFocus?: boolean;
}

export function TextInput({
  label,
  value,
  error,
  placeholder,
  faIcon,
  iconSrc,
  iconText,
  iconClassName,
  className,
  variant = "default",
  isRequired,
  onButtonClick,
  disabled,
  inputClassName,
  faIconClassName,
  faIconSize = "lg",
  helperText,
  helperTextClassName,
  helperCallback,
  pattern,
  showBorder = true,
  isFocusVisible = true,
  iconPosition = "right",
  autoFocus = false,
  ...props
}: TextInputProps) {
  return (
    <div className={`mb-2 ${className || ""}`}>
      {label && (
        <div className="flex items-center justify-between mb-1 px-1">
          <label className="block text-[#A9A9A9] mb-1 ml-1 font-medium">
            {label} {isRequired && <span className="text-red-500">*</span>}
          </label>
          {helperText && (
            <Button
              type="button"
              onClick={helperCallback}
              className={`text-xs font-medium text-white underline hover:text-[#FF94B1] ${helperTextClassName || ""}`}
            >
              {helperText}
            </Button>
          )}
        </div>
      )}
      <div className="relative">
        <input
          {...props}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`w-full rounded-lg transition text-white 
            ${isFocusVisible ? `focus:outline-none focus:ring-2 focus:ring-[#333333]` : "focus:outline-none"}  
            ${variant === "default" ? "bg-[#000000]" : "bg-[#1D1D1D]"} 
            ${iconPosition === "left" ? "pl-12 pr-4" : "pr-12 pl-4"}
             ${showBorder ? `border ${error ? "border-red-500" : "border-[#333333]"}` : ""} 
             ${inputClassName || "px-4 py-2 h-12"}`}
          pattern={pattern}
        />

        {(faIcon || iconSrc || iconText) && (
          <Button
            type="button"
            className={`absolute top-1/2 -translate-y-1/2 text-white
            ${iconPosition === "left" ? "left-3" : "right-3"}
              text-gray-400 ${iconClassName || ""} 
              flex items-center gap-1 text-sm`}
            onClick={() => onButtonClick?.(value)}
            disabled={!onButtonClick}
          >
            {!faIcon && iconSrc && (
              <img src={iconSrc} alt="icon" className="w-6 h-6" />
            )}
            {!iconSrc && faIcon && (
              <FontAwesomeIcon icon={faIcon} size={faIconSize} />
            )}
            {iconText && <span className="whitespace-nowrap">{iconText}</span>}
          </Button>
        )}
      </div>

      {error && <div className="text-red-500 text-sm mt-1 ml-2">{error}</div>}
    </div>
  );
}
